/* eslint-disable @typescript-eslint/consistent-type-assertions */
import RetweetIcon from '@bufferapp/ui/Icon/Icons/Retweet'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { AttachmentTypes } from '~publish/legacy/constants'
import type {
  Gif,
  Image,
  Link,
  Retweet,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'
import { parseTwitterLinks } from '~publish/legacy/utils/channels/twitter/helpers'
import { AttachmentVideo } from '../AttachmentVideo'
import { AttachmentRetweet } from '../AttachmentRetweet'
import type { TwitterContent, TwitterProfile } from '../../../types'
import { removeLinkFromEndOfText, truncateText } from '../../../Common/utils'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import * as Styles from './styles'
import type { LinkType } from '~publish/legacy/shared-components/LinkifiedText/LinkifiedText'

const getTextToDisplay = (
  hasLink: boolean,
  text?: string,
  link?: string | null,
): string | undefined => {
  let textToDisplay = text
  if (hasLink && textToDisplay) {
    textToDisplay = removeLinkFromEndOfText(textToDisplay, link)
  }
  textToDisplay = truncateText(textToDisplay, 280)

  return textToDisplay
}

interface GetMediaPropertiesResult {
  hasImages: boolean | number | undefined | null | Gif
  hasVideo: boolean
  hasLink: boolean
  isQuoteTweet: boolean
  isRetweet: boolean
}

const getMediaProperties = (
  content: TwitterContent,
  linkPreview: Link | null,
): GetMediaPropertiesResult => {
  const { text, images, gif, video, enabledAttachmentType, retweet } = content

  const hasText = !!text
  const hasMedia = enabledAttachmentType === AttachmentTypes.MEDIA
  const hasImages = hasMedia && (images?.length || gif)
  const hasVideo = !!(hasMedia && video)
  const hasLink = !!(linkPreview && !hasImages && !hasVideo)
  const hasContent = hasText || hasMedia || hasLink
  // If there is no content, we display the whole tweet as a retweet.
  const isQuoteTweet =
    enabledAttachmentType === AttachmentTypes.RETWEET && hasContent
  const isRetweet =
    enabledAttachmentType === AttachmentTypes.RETWEET && !hasContent

  // For simple retweets (isRetweet = true), check if the original tweet has media
  const retweetHasImages = !!(
    isRetweet && retweet?.media?.some((m) => m.type === 'photo')
  )
  const retweetHasVideo = !!(
    isRetweet &&
    retweet?.media?.some((m) => m.type === 'video' || m.type === 'animated_gif')
  )

  return {
    hasImages: hasImages || retweetHasImages,
    hasVideo: hasVideo || retweetHasVideo,
    hasLink,
    isQuoteTweet,
    isRetweet,
  }
}

export const Tweet = ({
  content,
  profile,
  index,
}: {
  content: TwitterContent
  profile: TwitterProfile
  index: number
}): JSX.Element => {
  const {
    text,
    images,
    gif,
    retweet,
    video,
    link: linkFromPost,
    inThread,
  } = content
  const { avatar, username, formattedUsername, displayName, isTwitterPremium } =
    profile
  const [linkPreview, linkText] = useLinkPreviewEffect({
    linkFromPost,
    text,
  })

  const { hasImages, hasVideo, hasLink, isQuoteTweet, isRetweet } =
    getMediaProperties(content, linkPreview)

  const {
    avatarUrl: retweetAvatar,
    text: retweetText,
    userDisplayName: retweetDisplayName,
    userName: retweetUsername,
  } = retweet || {}

  const originalText = isRetweet ? retweetText : text
  let textToDisplay = getTextToDisplay(hasLink, originalText, linkText)
  if (isTwitterPremium && (originalText?.length ?? 0) > 280) {
    textToDisplay = `${textToDisplay}... Read more`
  }

  // For retweets, use the original tweet's media; otherwise use current post's media
  let allImages: Image[] = []
  let videoToDisplay: Video | null = null

  if ((isRetweet || isQuoteTweet) && retweet?.media) {
    const retweetImages: Image[] = []
    retweet.media.forEach((mediaItem) => {
      if (mediaItem.type === 'photo') {
        retweetImages.push({
          url: mediaItem.url,
          width: mediaItem.width,
          height: mediaItem.height,
          altText: null,
          source: null,
          mediaType: 'image',
          originalUrl: null,
        })
      } else if (
        mediaItem.type === 'video' ||
        mediaItem.type === 'animated_gif'
      ) {
        videoToDisplay = {
          uploadId: mediaItem.id,
          name: mediaItem.type === 'animated_gif' ? 'GIF' : 'Video',
          duration: mediaItem.duration_ms ? mediaItem.duration_ms / 1000 : 0,
          durationMs: mediaItem.duration_ms || 0,
          size: 0,
          width: mediaItem.width,
          height: mediaItem.height,
          url: mediaItem.url,
          originalUrl: mediaItem.url,
          thumbnail: mediaItem.url,
          availableThumbnails: [mediaItem.url],
          thumbOffset: 0,
          mediaType: 'video',
        }
      }
    })
    allImages = retweetImages
  } else {
    allImages = images?.length ? images : ([{ ...gif }] as Image[])
    videoToDisplay = video || null
  }

  const usernameToDisplay = displayName || username

  return (
    <>
      {isRetweet && (
        <Styles.RetweetLabel>
          <RetweetIcon size="medium" />
          {` ${usernameToDisplay} Retweeted`}
        </Styles.RetweetLabel>
      )}
      <Styles.Wrapper withBorder={!!inThread} data-thread-id={index}>
        <Styles.Avatar
          data-testid="avatar"
          src={isRetweet ? retweetAvatar : avatar}
        />
        <Styles.UsernameWrapper>
          <Styles.Username>
            {isRetweet ? retweetDisplayName : usernameToDisplay}
          </Styles.Username>
          <Styles.FormattedUsername>
            {isRetweet ? `@${retweetUsername}` : formattedUsername}
          </Styles.FormattedUsername>
        </Styles.UsernameWrapper>
        <Styles.Content>
          <LinkifiedText
            links={parseTwitterLinks(textToDisplay) as LinkType[]}
            whitespace="pre-wrap"
          >
            {textToDisplay}
          </LinkifiedText>
        </Styles.Content>
        {hasImages && (
          <Styles.ImagesGrid
            images={allImages}
            isRowFormat={false}
            maxHeight="180px"
            shouldShowGifTag={false}
          />
        )}
        {hasVideo && videoToDisplay && (
          <AttachmentVideo video={videoToDisplay} />
        )}
        {hasLink && !isQuoteTweet && (
          <Styles.LinkPreview
            link={linkPreview as Link}
            clickable={false}
            imageCover
          />
        )}
        {isQuoteTweet && <AttachmentRetweet data={retweet as Retweet} />}
      </Styles.Wrapper>
    </>
  )
}
